import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Network, Search, Sparkle } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import { useGetIntegrationsProviders } from 'hooks/use-get-integrations';
import { useDeleteIntegration } from 'hooks/use-delete-integration';
import { useInitiateOAuthConnection } from 'hooks/use-initiate-oauth-connection';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { IntegrationBrowserDialog } from 'pages/integrations/components/integration-browser-dialog';
import IntegrationCard, { resolveProviderKey } from 'pages/integrations/components/IntegrationCard';
import { Navigate, useSearchParams } from 'react-router-dom';
import type { IntegrationProviders } from 'services/integration-service';
import { completeConnection, getActiveIntegrationConnections } from 'services/integration-service';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import { CubeBandage } from 'iconoir-react';
import ConfigurationHeading from 'components/configuration/ConfigurationHeading';

type FilterKey = 'all' | 'enabled' | 'coming-soon';

const getStatusLabel = (filterKey: FilterKey, t: (key: string) => string) => {
  switch (filterKey) {
    case 'enabled':
      return t('AVAILABLE_NOW') || 'Available now';
    case 'coming-soon':
      return t('COMING_SOON') || 'Coming soon';
    default:
      return t('ALL') || 'All';
  }
};

const statusOrder: Record<string, number> = {
  enabled: 0,
  active: 0,
  beta: 1,
  disabled: 2,
};

const featuredProviderIds = new Set([
  'google-drive',
  'gmail',
  'outlook-email',
  'outlook-calendar',
  'microsoft-sharepoint',
  'confluence',
  'jira',
  'onedrive',
]);

const getMatchesFilter = (provider: IntegrationProviders, filter: FilterKey) => {
  if (filter === 'all') return true;
  if (filter === 'enabled') return provider.status === 'enabled' || provider.status === 'active';
  return provider.status !== 'enabled' && provider.status !== 'active';
};

const sortProviders = (providers: IntegrationProviders[]) =>
  [...providers].sort((left, right) => {
    const statusDelta =
      (statusOrder[left.status] ?? Number.MAX_SAFE_INTEGER) -
      (statusOrder[right.status] ?? Number.MAX_SAFE_INTEGER);

    if (statusDelta !== 0) {
      return statusDelta;
    }

    return left.display_name.localeCompare(right.display_name);
  });

const integrationsGridClassName =
  'grid min-w-0 grid-cols-1 items-start gap-3 md:grid-cols-2 lg:grid-cols-3';

const IntegrationGridSkeleton = () => (
  <div className={integrationsGridClassName}>
    {Array.from({ length: 8 }).map((_, index) => (
      <Card
        key={index}
        className="flex w-full min-w-0 flex-col gap-3 rounded-lg border border-grey-50 bg-white p-4 shadow-none sm:p-6 dark:border-border dark:bg-card"
      >
        <div className="flex w-full items-center justify-between">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
        <div className="flex gap-[7px]">
          <Skeleton className="h-6 w-16 rounded" />
          <Skeleton className="h-6 w-20 rounded" />
        </div>
      </Card>
    ))}
  </div>
);

const Section = ({
  busyProviderId,
  getProviderConnectedState,
  canProviderBrowse,
  providers,
  onConnect,
  onOpenBrowser,
  onDisconnect,
}: {
  busyProviderId: string | null;
  getProviderConnectedState: (provider: IntegrationProviders) => boolean;
  canProviderBrowse: (provider: IntegrationProviders) => boolean;
  providers: IntegrationProviders[];
  onConnect: (providerId: string) => void;
  onOpenBrowser: (provider: IntegrationProviders) => void;
  onDisconnect: (provider: IntegrationProviders) => void;
}) => {
  if (providers.length === 0) {
    return null;
  }

  return (
    <div className={integrationsGridClassName}>
      {providers.map((provider) => (
        <IntegrationCard
          key={`${resolveProviderKey(provider)}-${provider.display_name}`}
          isBusy={busyProviderId === provider.provider_id}
          canBrowse={canProviderBrowse(provider)}
          isConnected={getProviderConnectedState(provider)}
          provider={provider}
          onConnect={onConnect}
          onOpenBrowser={onOpenBrowser}
          onDisconnect={onDisconnect}
        />
      ))}
    </div>
  );
};

export function IntegrationsRedirect() {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('tab', 'integrations');
  return <Navigate to={{ pathname: '/knowledge-base', search: `?${next.toString()}` }} replace />;
}

export default function IntegrationsPage({
  embedded = false,
  search: controlledSearch,
  onSearchChange,
}: {
  embedded?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [internalSearch, setInternalSearch] = useState('');
  const search = controlledSearch ?? internalSearch;
  const setSearch = onSearchChange ?? setInternalSearch;
  const showSearch = controlledSearch === undefined;
  const [busyProviderId, setBusyProviderId] = useState<string | null>(null);
  const [connectedByProviderId, setConnectedByProviderId] = useState<Record<string, string>>({});
  const [integrationBrowserState, setIntegrationBrowserState] = useState<{
    connectionId: string | null;
    open: boolean;
    providerId: string;
    providerName: string;
  }>({
    connectionId: null,
    open: false,
    providerId: '',
    providerName: t('INTEGRATION') || 'Integration',
  });
  const [oauthPopup, setOauthPopup] = useState<Window | null>(null);
  const processedOAuthCallbackRef = useRef<string | null>(null);
  const { data: providers = [], isLoading, isError, error, refetch } = useGetIntegrationsProviders();
  const { initiateOAuth } = useInitiateOAuthConnection();
  const { mutateAsync: deleteIntegration } = useDeleteIntegration();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const keepTabParams = (params: Record<string, string> = {}) => {
    const next = new URLSearchParams(params);
    const tab = searchParams.get('tab');
    if (tab) {
      next.set('tab', tab);
    }
    return next;
  };
  const browsableProviderIds = useMemo(
    () => new Set(providers.filter((provider) => provider.resource_browse_supported).map((provider) => provider.provider_id)),
    [providers]
  );

  const getProviderConnectionId = useCallback(
    (provider: IntegrationProviders) => provider.connection_id || connectedByProviderId[provider.provider_id],
    [connectedByProviderId]
  );

  const getProviderConnectedState = useCallback(
    (provider: IntegrationProviders) => Boolean(getProviderConnectionId(provider)),
    [getProviderConnectionId]
  );

  const canProviderBrowse = useCallback(
    (provider: IntegrationProviders) =>
      getProviderConnectedState(provider) &&
      provider.resource_browse_supported,
    [getProviderConnectedState]
  );

  const syncActiveConnections = useCallback(async () => {
    if (!configurationSettings) {
      return;
    }

    const activeConnections = await getActiveIntegrationConnections(configurationSettings);
    const nextConnectedByProviderId = activeConnections.reduce<Record<string, string>>((acc, connection) => {
      if (connection.status === 'active') {
        acc[connection.provider_id] = connection.id;
      }
      return acc;
    }, {});

    setConnectedByProviderId(nextConnectedByProviderId);
  }, [configurationSettings]);

  const handleOAuthCallback = useCallback(async (params: Record<string, string>) => {
    try {
      const flowId = params.flow_id;
      const oauthStatus = params.status;
      const sourceConnectionId = params.source_connection_id;
      const errorParam = params.error;

      if (oauthStatus === 'error' || errorParam) {
        setBusyProviderId(null);
        setSearchParams(keepTabParams(), { replace: true });
        toast({
          title: t('CONNECTION_FAILED') || 'Connection failed',
          description: errorParam || t('OAUTH_AUTHORIZATION_FAILED') || 'OAuth authorization failed',
          variant: 'destructive',
        });
        return;
      }

      if (!flowId || !sourceConnectionId) {
        setBusyProviderId(null);
        setSearchParams(keepTabParams(), { replace: true });
        toast({
          title: t('CONNECTION_FAILED') || 'Connection failed',
          description: t('MISSING_OAUTH_PARAMETERS') || 'Missing required OAuth parameters',
          variant: 'destructive',
        });
        return;
      }

      const sessionData = sessionStorage.getItem(`oauth_flow_${flowId}`);
      if (!sessionData) {
        setBusyProviderId(null);
        setSearchParams(keepTabParams(), { replace: true });
        toast({
          title: t('CONNECTION_FAILED') || 'Connection failed',
          description: t('OAUTH_SESSION_EXPIRED') || 'OAuth flow session expired or not found',
          variant: 'destructive',
        });
        return;
      }

      const { connection_id, provider_id, owner_type } = JSON.parse(sessionData);

      if (!configurationSettings) {
        throw new Error('Configuration settings not available');
      }

      const response = await completeConnection(configurationSettings, {
        provider_id,
        owner_type,
        params: {
          connection_id,
          source_connection_id: sourceConnectionId,
        },
      });

      sessionStorage.removeItem(`oauth_flow_${flowId}`);
      setConnectedByProviderId((current) => ({
        ...current,
        [provider_id]: response.id,
      }));
      setBusyProviderId(null);
      setSearchParams(keepTabParams(), { replace: true });

      toast({
        variant: 'success',
        title: t('CONNECTION_SUCCESSFUL') || 'Connection successful',
        description:
          t('INTEGRATION_CONNECTED_SUCCESSFULLY', {
            name: response.provider_family || t('INTEGRATION') || 'Integration',
            defaultValue: '{{name}} has been connected successfully',
          }) ||
          `${response.provider_family || 'Integration'} has been connected successfully`,
      });

      if (response.id && response.provider_id && browsableProviderIds.has(response.provider_id)) {
        setIntegrationBrowserState({
          connectionId: response.id,
          open: true,
          providerId: response.provider_id,
          providerName: response.display_name || t('INTEGRATION') || 'Integration',
        });
      }

      if (oauthPopup && !oauthPopup.closed) {
        oauthPopup.postMessage({ type: 'OAUTH_CALLBACK_ACK' }, window.location.origin);
        oauthPopup.close();
      }

      void syncActiveConnections();
      void refetch();
    } catch (err) {
      setBusyProviderId(null);
      setSearchParams(keepTabParams(), { replace: true });
      const errorMsg =
        err instanceof Error ? err.message : t('FAILED_TO_COMPLETE_CONNECTION') || 'Failed to complete connection';
      toast({
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description: errorMsg,
        variant: 'destructive',
      });
    }
  }, [browsableProviderIds, configurationSettings, oauthPopup, refetch, setSearchParams, syncActiveConnections, t, toast]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'OAUTH_CALLBACK') {
        setSearchParams(keepTabParams(event.data.params), { replace: true });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setSearchParams]);

  useEffect(() => {
    let isCancelled = false;

    const loadActiveConnections = async () => {
      if (!configurationSettings) {
        return;
      }

      try {
        const activeConnections = await getActiveIntegrationConnections(configurationSettings);
        if (isCancelled) {
          return;
        }

        const nextConnectedByProviderId = activeConnections.reduce<Record<string, string>>((acc, connection) => {
          if (connection.status === 'active') {
            acc[connection.provider_id] = connection.id;
          }
          return acc;
        }, {});

        setConnectedByProviderId(nextConnectedByProviderId);
      } catch {
        if (!isCancelled) {
          setConnectedByProviderId({});
        }
      }
    };

    void loadActiveConnections();

    return () => {
      isCancelled = true;
    };
  }, [configurationSettings]);

  useEffect(() => {
    const flowId = searchParams.get('flow_id');
    const oauthStatus = searchParams.get('status');
    const sourceConnectionId = searchParams.get('source_connection_id');
    const errorParam = searchParams.get('error');

    if (!flowId && !oauthStatus && !sourceConnectionId && !errorParam) {
      return;
    }

    const callbackKey = [flowId, oauthStatus, sourceConnectionId, errorParam].join('|');
    if (processedOAuthCallbackRef.current === callbackKey) {
      return;
    }

    processedOAuthCallbackRef.current = callbackKey;
    const storedFlow = flowId ? sessionStorage.getItem(`oauth_flow_${flowId}`) : null;
    if (storedFlow) {
      const { provider_id } = JSON.parse(storedFlow) as { provider_id: string };
      setBusyProviderId(provider_id);
    }

    void handleOAuthCallback({
      ...(flowId ? { flow_id: flowId } : {}),
      ...(oauthStatus ? { status: oauthStatus } : {}),
      ...(sourceConnectionId ? { source_connection_id: sourceConnectionId } : {}),
      ...(errorParam ? { error: errorParam } : {}),
    });
  }, [handleOAuthCallback, searchParams]);

  const handleConnect = async (providerId: string) => {
    try {
      if (window.location.search) {
        setSearchParams(keepTabParams(), { replace: true });
      }

      setBusyProviderId(providerId);

      const response = await initiateOAuth({
        providerId,
        ownerType: 'user',
      });

      if (response?.redirect_url) {
        const popup = window.open(response.redirect_url, '_blank', 'width=600,height=700');
        setOauthPopup(popup);
      } else {
        setBusyProviderId(null);
        toast({
          title: t('CONNECTION_FAILED') || 'Connection failed',
          description: t('FAILED_TO_INITIATE_OAUTH_FLOW') || 'Failed to initiate OAuth flow',
          variant: 'destructive',
        });
      }
    } catch (err) {
      setBusyProviderId(null);
      toast({
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          err instanceof Error ? err.message : t('FAILED_TO_CONNECT_INTEGRATION') || 'Failed to connect integration',
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = useCallback(async (provider: IntegrationProviders) => {
    const connectionId = getProviderConnectionId(provider);
    if (!connectionId) {
      toast({
        title: t('DISCONNECT_UNAVAILABLE') || 'Disconnect unavailable',
        description:
          t('CONNECTION_SYNCING_TRY_AGAIN') ||
          'Connection details are still syncing. Please try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setBusyProviderId(provider.provider_id);
      await deleteIntegration({
        connectionId,
      });

      setConnectedByProviderId((current) => {
        const next = { ...current };
        delete next[provider.provider_id];
        return next;
      });
      setBusyProviderId(null);

      toast({
        variant: 'success',
        title: t('DISCONNECTED') || 'Disconnected',
        description:
          t('INTEGRATION_DISCONNECTED_SUCCESSFULLY', {
            name: provider.display_name,
            defaultValue: '{{name}} has been disconnected successfully',
          }) || `${provider.display_name} has been disconnected successfully`,
      });

      void syncActiveConnections();
      void refetch();
    } catch (err) {
      setBusyProviderId(null);
      toast({
        title: t('DISCONNECT_FAILED') || 'Disconnect failed',
        description:
          err instanceof Error ? err.message : t('FAILED_TO_DISCONNECT_INTEGRATION') || 'Failed to disconnect integration',
        variant: 'destructive',
      });
    }
  }, [deleteIntegration, getProviderConnectionId, refetch, syncActiveConnections, t, toast]);

  const handleOpenBrowser = useCallback((provider: IntegrationProviders) => {
    const connectionId = getProviderConnectionId(provider);
    if (!connectionId) {
      toast({
        title: t('BROWSE_UNAVAILABLE') || 'Browse unavailable',
        description:
          t('CONNECTION_SYNCING_TRY_AGAIN') ||
          'Connection details are still syncing. Please try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    setIntegrationBrowserState({
      connectionId,
      open: true,
      providerId: provider.provider_id,
      providerName: provider.display_name || t('INTEGRATION') || 'Integration',
    });
  }, [getProviderConnectionId, t, toast]);

  const handleIntegrationBrowserOpenChange = useCallback((open: boolean) => {
    setIntegrationBrowserState((current) => ({
      ...current,
      connectionId: open ? current.connectionId : null,
      open,
      providerId: open ? current.providerId : '',
    }));
  }, []);

  const handleIntegrationSyncStarted = useCallback(() => {
    void syncActiveConnections();
    void refetch();
  }, [refetch, syncActiveConnections]);

  const filteredProviders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return sortProviders(
      providers.filter((provider) => {
        if (!getMatchesFilter(provider, activeFilter)) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const haystack = [
          provider.display_name,
          provider.provider_id,
          provider.provider_family,
          provider.status,
          ...(provider.supported_capabilities || []),
          ...(provider.supported_scopes || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(normalizedSearch);
      })
    );
  }, [activeFilter, providers, search]);

  const featuredProviders = filteredProviders.filter((provider) =>
    featuredProviderIds.has(resolveProviderKey(provider))
  );
  const allOtherProviders = filteredProviders.filter(
    (provider) => !featuredProviderIds.has(resolveProviderKey(provider))
  );

  const hasProviders = filteredProviders.length > 0;

  const integrationCountLabel =
    filteredProviders.length === 1
      ? t('INTEGRATION') || 'integration'
      : t('INTEGRATIONS') || 'integrations';

  const content = (
    <>
      <IntegrationBrowserDialog
        connectionId={integrationBrowserState.connectionId}
        onOpenChange={handleIntegrationBrowserOpenChange}
        onSyncStarted={handleIntegrationSyncStarted}
        open={integrationBrowserState.open}
        providerId={integrationBrowserState.providerId}
        providerName={integrationBrowserState.providerName}
      />

      {!embedded && (
        <ConfigurationHeader title={t('INTEGRATIONS_KEY') || 'Integrations'} subtitle={t('INTEGRATIONS_PAGE_DESCRIPTION') || 'Connect external tools to retrieve data, browse resources, and extend your workspace.'} icon={<CubeBandage width={20} height={20} strokeWidth={1.75} className="text-grey-900 dark:text-foreground" />} />
      )}

      {showSearch && (
        <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
            <Search className="h-[15px] w-[15px] text-grey-400" />
          </div>
          <Input
            className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('SEARCH_INTEGRATIONS') || 'Search integrations'}
            value={search}
          />
        </div>
      )}

      <div className="flex min-w-0 flex-col gap-6">
        <div className="inline-flex flex-wrap items-center gap-2" role="tablist">
          {(['all', 'enabled', 'coming-soon'] as FilterKey[]).map((filterKey) => {
            const isActive = activeFilter === filterKey;

            return (
              <button
                key={filterKey}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveFilter(filterKey)}
                className={cn(
                  'inline-flex h-10 items-center rounded-lg border px-3 text-sm font-normal leading-[1.2] transition-colors',
                  isActive
                    ? 'border-grey-100 bg-grey-50 text-grey-900 dark:border-true-white/40 dark:bg-true-white/15 dark:text-true-white'
                    : 'border-transparent text-grey-500 hover:bg-grey-50 hover:text-grey-900 dark:text-true-white/75 dark:hover:bg-true-white/10 dark:hover:text-true-white'
                )}
              >
                {getStatusLabel(filterKey, t)}
              </button>
            );
          })}
        </div>

        {isLoading ? (
          <IntegrationGridSkeleton />
        ) : isError ? (
          <Card className="rounded-lg border border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
            <div className="flex flex-col gap-3 p-6 text-sm text-muted-foreground">
              <span>
                {error instanceof Error
                  ? error.message
                  : t('FAILED_TO_FETCH_INTEGRATIONS') ||
                  'Something went wrong while fetching integrations.'}
              </span>
              <div>
                <Button className="h-8 px-3" onClick={() => void refetch()} size="sm" type="button" variant="outline">
                  {t('TRY_AGAIN') || 'Try again'}
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <>
            <div className="flex min-w-0 flex-col gap-3 border-b border-grey-100 pb-8 dark:border-border">
              <div className="flex min-w-0 items-center gap-2">
                <Sparkle className="h-4 w-4 shrink-0 text-grey-900 dark:text-foreground" />
                <ConfigurationHeading title={t('POPULAR_INTEGRATIONS') || 'Popular integrations'} />
                <Separator className="min-w-4 flex-1" />
              </div>

              {featuredProviders.length > 0 ? (
                <div className={integrationsGridClassName}>
                  {featuredProviders.map((provider) => (
                    <IntegrationCard
                      key={`featured-${resolveProviderKey(provider)}-${provider.display_name}`}
                      isBusy={busyProviderId === provider.provider_id}
                      canBrowse={canProviderBrowse(provider)}
                      isConnected={getProviderConnectedState(provider)}
                      provider={provider}
                      onConnect={handleConnect}
                      onOpenBrowser={handleOpenBrowser}
                      onDisconnect={handleDisconnect}
                    />
                  ))}
                </div>
              ) : (
                <Card className="rounded-lg border border-dashed border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
                  <div className="p-6 text-sm text-muted-foreground">
                    {t('NO_FEATURED_INTEGRATIONS_MATCHED') ||
                      'No featured integrations matched the current filter.'}
                  </div>
                </Card>
              )}
            </div>

            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <ConfigurationHeading title={t('ALL_INTEGRATIONS') || 'All integrations'} />
                <span className="shrink-0 text-grey-100 dark:text-border">|</span>
                <Separator className="min-w-4 flex-1" />
              </div>

              {hasProviders ? (
                allOtherProviders.length > 0 ? (
                  <Section
                    busyProviderId={busyProviderId}
                    canProviderBrowse={canProviderBrowse}
                    getProviderConnectedState={getProviderConnectedState}
                    providers={allOtherProviders}
                    onConnect={handleConnect}
                    onOpenBrowser={handleOpenBrowser}
                    onDisconnect={handleDisconnect}
                  />
                ) : (
                  <Card className="rounded-lg border border-dashed border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
                    <div className="p-6 text-xs 2xl:text-sm text-muted-foreground">
                      {t('NO_OTHER_INTEGRATIONS_MATCHED') ||
                        'All matching integrations are shown in Popular integrations.'}
                    </div>
                  </Card>
                )
              ) : (
                <Card className="flex h-[315px] w-full flex-col items-center justify-center gap-6 rounded-lg border border-grey-100 bg-white p-6 shadow-none dark:border-border dark:bg-card">
                  <div className="flex flex-col items-center gap-1.5 text-center">
                    <Network className="h-5 w-5 text-grey-500 dark:text-muted-foreground" />
                    <p className="text-base font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                      {search.trim()
                        ? t('NO_INTEGRATIONS_MATCHED_SEARCH') || 'No integrations matched your search.'
                        : t('NO_INTEGRATIONS_AVAILABLE') || 'No integrations available.'}
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return <ConfigurationLayout>{content}</ConfigurationLayout>;
}
